import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { trpc } from '@/lib/trpc'
import { useUpgradeRequiredDialog } from '@/hooks/useUpgradeRequiredDialog'
import { activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@deepguide-ai/ui/dialog'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Sparkles, Lock } from 'lucide-react'
import { toast } from 'sonner'
import type { Plan } from '@deepguide-ai/types'
import {
  AUDIT_TYPES_BY_FRAMEWORK,
  FRAMEWORK_ENROLLMENT_FLAGS,
  ONBOARDING_FRAMEWORK_KEYS,
  getAuditTypeLabel,
  getFrameworkDisplayName,
} from '@deepguide-ai/types'
import { useFeatureFlags } from '@/hooks/useFeatureFlag'
import { CheckboxInput } from '@/components/screenata/wizard-inputs'
import { TSC_OPTIONS } from '@/components/screenata/onboarding/TrustCriteriaInput'
import {
  ONBOARDING_HIPAA_ENTITY_TYPE_OPTIONS,
  type OnboardingHipaaEntityTypeValue,
} from '@deepguide-ai/types'

// RFC-156 Option A: every framework users can create cycles against.
// Enrollment is implicit — picking a framework here auto-enrolls the
// workspace in it via createAuditCycle's auto-enroll path.
// The audit-type names come from the shared vocabulary; only these extra
// clarifiers are dialog-specific, so they're the only thing restated here.
const AUDIT_TYPE_HINTS: Record<string, string> = {
  TYPE_I: 'Point in time',
  TYPE_II: 'Over a period',
}

interface FrameworkEntry {
  key: string
  name: string
  auditTypes: Array<{ value: string; label: string }>
}

function frameworkEntry(key: string): FrameworkEntry {
  return {
    key,
    name: getFrameworkDisplayName(key),
    auditTypes: (AUDIT_TYPES_BY_FRAMEWORK[key] ?? []).map((value) => {
      const hint = AUDIT_TYPE_HINTS[value]
      return {
        value,
        label: `${getAuditTypeLabel(value, key)}${hint ? ` (${hint})` : ''}`,
      }
    }),
  }
}

// Derived from the shared framework vocabulary rather than a local list. The
// hardcoded ['SOC2','HIPAA','ISO27001'] this replaced had no ISO42001 entry,
// so opening an ISO 42001 cycle fell through to SUPPORTED_FRAMEWORKS[0] and
// the dialog believed it was editing a SOC 2 audit: it showed the SOC 2
// framework, SOC 2 audit types, and the SOC-2-only trust-service-criteria
// block — and Save would have written TYPE_I plus TSC onto the cycle.
const SUPPORTED_FRAMEWORKS: FrameworkEntry[] = [...ONBOARDING_FRAMEWORK_KEYS]
  // A framework with no audit types would render an empty, unusable dropdown.
  // Drop it rather than offer a dead option.
  .filter((key) => (AUDIT_TYPES_BY_FRAMEWORK[key]?.length ?? 0) > 0)
  .map(frameworkEntry)

interface CreateAuditCycleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When provided, the dialog edits this cycle instead of creating a new one. */
  cycle?: {
    id: string
    label: string
    auditType: string
    periodStart: string | Date
    periodEnd: string | Date
    targetAuditDate?: string | Date | null
    auditorId?: string | null
    framework?: string | null
  } | null
}

function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

// Default audit period length. SOC 2 Type I is a point-in-time report with a
// shorter readiness window (3-6 months); everything else defaults to a full
// 12-month period.
function defaultPeriodMonths(framework: string, auditType: string): number {
  return framework === 'SOC2' && auditType === 'TYPE_I' ? 6 : 12
}

function resetForm(defaultFramework: string) {
  const fw =
    SUPPORTED_FRAMEWORKS.find((f) => f.key === defaultFramework) ??
    SUPPORTED_FRAMEWORKS[0]
  const auditType = fw.auditTypes[0]?.value ?? 'TYPE_I'
  const periodStart = startOfToday()
  return {
    label: '',
    framework: fw.key,
    auditType,
    periodStart: periodStart as Date | undefined,
    periodEnd: addMonths(
      periodStart,
      defaultPeriodMonths(fw.key, auditType),
    ) as Date | undefined,
    auditorId: undefined as string | undefined,
    asOfDate: undefined as Date | undefined,
    // #613: SOC 2 trust-service-criteria scope, declared on the cycle.
    trustServiceCriteria: ['Security'] as string[],
    // #613: HIPAA entity type, declared on the cycle (HIPAA cycles only).
    hipaaEntityType: 'COVERED_ENTITY' as OnboardingHipaaEntityTypeValue,
  }
}

export function CreateAuditCycleDialog({
  open,
  onOpenChange,
  cycle,
}: CreateAuditCycleDialogProps) {
  const isEditMode = Boolean(cycle)
  const [
    {
      label,
      framework,
      auditType,
      periodStart,
      periodEnd,
      asOfDate,
      auditorId,
      trustServiceCriteria,
      hipaaEntityType,
    },
    setForm,
  ] = useState(() => resetForm(SUPPORTED_FRAMEWORKS[0].key))
  const queryClient = useQueryClient()
  const previousOpenRef = useRef(false)

  // #171 / RFC-216 M3: ISO 27001 and ISO 42001 are gated behind their own
  // flags. Hide a gated option unless its flag is on; while the flags load,
  // treat them as off to avoid a flash of an option the user can't create.
  // Driven off the shared gate table, so a newly gated framework needs no
  // change here.
  const { flags: gatedFlags, loading: flagsLoading } = useFeatureFlags(
    Object.values(FRAMEWORK_ENROLLMENT_FLAGS),
  )
  const editingFramework = cycle?.framework
  const availableFrameworks = useMemo(
    () =>
      SUPPORTED_FRAMEWORKS.filter((f) => {
        const flag = FRAMEWORK_ENROLLMENT_FLAGS[f.key]
        if (!flag) return true
        // Keep a gated framework visible when editing a cycle already on it,
        // so the (disabled) picker still renders its current value; the gate
        // is on NEW enrollment, not on existing programs.
        if (editingFramework === f.key) return true
        return gatedFlags[flag] === true && !flagsLoading
      }),
    [gatedFlags, flagsLoading, editingFramework],
  )
  // #813: No-PHI acknowledgment, required when scoping a workspace into HIPAA
  // before a BAA enables PHI.
  const [noPhiAck, setNoPhiAck] = useState(false)

  // RFC-156: which frameworks are already enrolled? Drives the "auto-enroll
  // notice" we show when the user picks a framework that isn't enrolled yet.
  const enrolledQuery = useQuery(
    trpc.complianceProgram.listEnrolledFrameworks.queryOptions(),
  )
  const enrolledKeys = useMemo(() => {
    const frameworks = (enrolledQuery.data?.frameworks ?? []) as Array<{
      key: string
    }>
    return new Set(frameworks.map((f) => f.key))
  }, [enrolledQuery.data])

  // Never coerce an unrecognized framework to SUPPORTED_FRAMEWORKS[0] — that
  // silently relabels the cycle as SOC 2 and re-enables the SOC-2-only
  // controls below. Synthesize an entry from the key we were given instead.
  const selectedFramework =
    SUPPORTED_FRAMEWORKS.find((f) => f.key === framework) ??
    frameworkEntry(framework)
  const willAutoEnroll = !isEditMode && !enrolledKeys.has(framework)

  // #615: framework billing allowance. Enrolling a NEW framework counts
  // against the plan's framework limit, so block the create + prompt an
  // upgrade when the workspace is at its limit and the picked framework
  // isn't enrolled yet. Re-running a cycle for an enrolled framework is free.
  const allowanceQuery = useQuery({
    ...trpc.complianceProgram.frameworkAllowance.queryOptions(),
    enabled: open && !isEditMode,
  })
  const allowance = allowanceQuery.data
  const atFrameworkLimit =
    willAutoEnroll && !!allowance && !allowance.canAddMore
  const { showUpgrade, showUpgradeForError, upgradeDialog } =
    useUpgradeRequiredDialog()
  const showFrameworkLimitUpgrade = () => {
    if (!allowance) return false

    return showUpgrade(
      {
        resource: 'frameworks',
        used: allowance.used,
        max: allowance.limit,
        plan: allowance.plan as Plan,
        message: `Your ${allowance.planName} plan includes ${allowance.limit} framework${allowance.limit === 1 ? '' : 's'}. Upgrade to run additional frameworks in this workspace.`,
      },
      {
        featureName: 'More compliance frameworks',
        source: 'create_audit_cycle_framework_limit',
      }
    )
  }

  // Edit mode: fetch the cycle detail so we can hydrate the (program-level)
  // trust-service-criteria scope into the TSC selector (#613).
  const cycleDetailQuery = useQuery({
    ...trpc.auditor.getAuditCycle.queryOptions({
      auditCycleId: cycle?.id ?? '',
    }),
    enabled: open && Boolean(cycle?.id),
  })

  // #613: HIPAA entity type is program-level. Read the current value to seed
  // the selector; persist via the existing setHipaaEntityType mutation (which
  // re-seeds the Privacy Officer role + recomputes Privacy-Rule necessity).
  const entityTypeQuery = useQuery({
    ...trpc.complianceProgram.getHipaaEntityType.queryOptions(),
    enabled: open,
  })
  const setEntityTypeMutation = useMutation(
    trpc.complianceProgram.setHipaaEntityType.mutationOptions(),
  )

  // #813: current PHI posture, to decide whether the No-PHI acknowledgment is
  // required before scoping this workspace into HIPAA.
  const activeWorkspaceId = useAtomValue(activeWorkspaceWithStorageAtom)?.id
  const phiStatusQuery = useQuery({
    ...trpc.workspaces.getPhiStatus.queryOptions(),
    // getPhiStatus reads the workspace from request context, not input — the
    // workspaceId here only busts the cache across workspace switches.
    queryKey: [
      ...trpc.workspaces.getPhiStatus.queryKey(),
      { workspaceId: activeWorkspaceId },
    ],
    enabled: open,
  })
  const acknowledgeNoPhiMutation = useMutation(
    trpc.workspaces.acknowledgeNoPhi.mutationOptions(),
  )
  const phiStatus = phiStatusQuery.data
  // The workspace may not be HIPAA-enrolled yet (this cycle enrolls it), so
  // gate on the selected framework rather than phiStatus.hipaaEnrolled.
  const showPhiAck =
    framework === 'HIPAA' &&
    !!phiStatus &&
    !phiStatus.phiEnabled &&
    !phiStatus.acknowledgedAt

  // Hydrate the form from `cycle` whenever the dialog opens.
  useEffect(() => {
    if (open && !previousOpenRef.current) {
      setNoPhiAck(false)
      if (cycle) {
        const cycleFramework = cycle.framework ?? SUPPORTED_FRAMEWORKS[0].key
        const fw = SUPPORTED_FRAMEWORKS.find((f) => f.key === cycleFramework)
        setForm({
          label: cycle.label,
          // The cycle's OWN framework, always. Falling back to the first
          // supported entry made an unrecognized framework editable as SOC 2.
          framework: cycleFramework,
          auditType:
            fw?.auditTypes.find((t) => t.value === cycle.auditType)?.value ??
            // Keep whatever is stored over resetting to another framework's
            // default — Save writes this value back.
            cycle.auditType ??
            fw?.auditTypes[0]?.value ??
            'TYPE_I',
          // A cycle with no period yet leaves the pickers empty. new Date(null)
          // is 1970-01-01, which passed the submit check and saved 1970→1970.
          periodStart: cycle.periodStart ? new Date(cycle.periodStart) : undefined,
          periodEnd: cycle.periodEnd ? new Date(cycle.periodEnd) : undefined,
          asOfDate: cycle.targetAuditDate ? new Date(cycle.targetAuditDate) : undefined,
          auditorId: cycle.auditorId ?? undefined,
          trustServiceCriteria: ['Security'],
          hipaaEntityType: 'COVERED_ENTITY',
        })
      } else {
        setForm(resetForm(SUPPORTED_FRAMEWORKS[0].key))
      }
    }
    previousOpenRef.current = open
  }, [open, cycle])

  // Seed TSC from the fetched cycle detail once it loads (edit mode).
  useEffect(() => {
    const tsc = cycleDetailQuery.data?.trustServiceCriteria
    if (open && cycle && Array.isArray(tsc) && tsc.length > 0) {
      setForm((current) => ({ ...current, trustServiceCriteria: tsc as string[] }))
    }
  }, [open, cycle, cycleDetailQuery.data])

  // Seed the HIPAA entity type from the program's current value once it loads.
  useEffect(() => {
    const entityType = entityTypeQuery.data?.entityType
    if (open && entityType) {
      setForm((current) => ({
        ...current,
        hipaaEntityType: entityType as OnboardingHipaaEntityTypeValue,
      }))
    }
  }, [open, entityTypeQuery.data])

  const auditorsQuery = useQuery(
    trpc.auditorGallery.listAuditors.queryOptions()
  )

  const createMutation = useMutation(
    trpc.auditor.createAuditCycle.mutationOptions()
  )
  const updateMutation = useMutation(
    trpc.auditor.updateAuditCycle.mutationOptions()
  )

  const isPending = isEditMode ? updateMutation.isPending : createMutation.isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!label || !periodStart || !periodEnd) return
    // #813: scoping into HIPAA without a BAA requires the No-PHI acknowledgment.
    if (showPhiAck && !noPhiAck) return
    // #615: enrolling a new framework beyond the plan allowance is blocked —
    // prompt an upgrade instead of attempting the create.
    if (atFrameworkLimit) {
      showFrameworkLimitUpgrade()
      return
    }

    try {
      // TSC is SOC 2-only; omit it for other frameworks (server ignores it).
      const tscPayload =
        framework === 'SOC2' ? trustServiceCriteria : undefined
      if (isEditMode && cycle) {
        await updateMutation.mutateAsync({
          auditCycleId: cycle.id,
          label,
          auditType: auditType as any,
          periodStart: periodStart.toISOString(),
          periodEnd: periodEnd.toISOString(),
          ...(auditType === 'TYPE_I' ? { targetAuditDate: (asOfDate ?? periodEnd).toISOString() } : {}),
          auditorId: auditorId ?? null,
          trustServiceCriteria: tscPayload,
        } as any)
        // HIPAA entity type is program-level — persist via its own mutation
        // (re-seeds the Privacy Officer role + recomputes Privacy-Rule
        // necessity). No-op when unchanged.
        if (framework === 'HIPAA') {
          await setEntityTypeMutation.mutateAsync({ entityType: hipaaEntityType })
        }
        toast.success('Audit updated')
      } else {
        await createMutation.mutateAsync({
          label,
          auditType: auditType as any,
          periodStart: periodStart.toISOString(),
          periodEnd: periodEnd.toISOString(),
          ...(auditType === 'TYPE_I' ? { targetAuditDate: (asOfDate ?? periodEnd).toISOString() } : {}),
          auditorId,
          framework,
          trustServiceCriteria: tscPayload,
        } as any)
        // Set the entity type after enrollment (createAuditCycle enrolls
        // HIPAA first; the entity type then adjusts roles/policies).
        if (framework === 'HIPAA') {
          await setEntityTypeMutation.mutateAsync({ entityType: hipaaEntityType })
        }
        toast.success(
          willAutoEnroll
            ? `Audit created. ${selectedFramework.name} is now enabled for this workspace`
            : 'Audit created',
        )
        setForm(resetForm(SUPPORTED_FRAMEWORKS[0].key))
      }
      // #813: record the No-PHI acknowledgment now that the workspace is in
      // HIPAA scope (createAuditCycle auto-enrolls HIPAA above).
      if (showPhiAck && noPhiAck) {
        await acknowledgeNoPhiMutation.mutateAsync()
        queryClient.invalidateQueries({
          queryKey: trpc.workspaces.getPhiStatus.queryKey(),
        })
      }
      queryClient.invalidateQueries({ queryKey: trpc.auditor.listAuditCycles.queryKey() })
      if (cycle) {
        queryClient.invalidateQueries({
          queryKey: trpc.auditor.getAuditCycle.queryKey({ auditCycleId: cycle.id }),
        })
      }
      // Refresh enrollment list in case we just auto-enrolled a new framework
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.listEnrolledFrameworks.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getHipaaEntityType.queryKey(),
      })
      onOpenChange(false)
    } catch (error) {
      if (
        showUpgradeForError(error, {
          featureName: 'More compliance frameworks',
          source: 'create_audit_cycle_framework_limit',
        })
      )
        return
      toast.error(
        error instanceof Error
          ? error.message
          : isEditMode
            ? 'Failed to update audit'
            : 'Failed to create audit'
      )
    }
  }

  const dialog = (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[600px] flex max-h-[85vh] flex-col gap-0 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold">
            {isEditMode ? 'Edit Audit' : 'Create Audit'}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-text-subtle">
            {isEditMode
              ? 'Update the audit name, period, audit type, or assigned auditor firm.'
              : 'Define a new audit to organize compliance evidence and invite auditors.'}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <ScrollArea
            className="min-h-0 flex-1"
            viewClassName="max-h-[calc(85vh-11rem)] [&>div]:!block"
          >
            <div className="space-y-4 px-6 pt-2 pb-6">
          <div className="space-y-1.5">
            <Label htmlFor="label">
              Audit name
            </Label>
            <Input
              id="label"
              placeholder="2026 Type II Audit"
              value={label}
              onChange={(e) =>
                setForm((current) => ({ ...current, label: e.target.value }))
              }
              className="h-9 rounded-sm border-border-divider text-[12px]"
              required
            />
          </div>
          {/* RFC-156: framework picker. Auto-enrolls the workspace in
              whichever framework the user picks when the cycle is created. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Framework</Label>
              <Select
                value={framework}
                onValueChange={(value) => {
                  const fw =
                    SUPPORTED_FRAMEWORKS.find((f) => f.key === value) ??
                    SUPPORTED_FRAMEWORKS[0]
                  setForm((current) => {
                    // Reset audit type to the new framework's default since
                    // SOC 2 and HIPAA use different vocabularies.
                    const nextAuditType =
                      fw.auditTypes[0]?.value ?? current.auditType
                    const start = current.periodStart ?? startOfToday()
                    return {
                      ...current,
                      framework: fw.key,
                      auditType: nextAuditType,
                      // Re-default the period to match the new framework/type.
                      periodStart: start,
                      periodEnd: addMonths(
                        start,
                        defaultPeriodMonths(fw.key, nextAuditType),
                      ),
                    }
                  })
                }}
                disabled={isEditMode}
              >
                <SelectTrigger fullWidth className="h-9 border-border-divider text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableFrameworks.map((fw) => (
                    <SelectItem
                      key={fw.key}
                      value={fw.key}
                      className="text-[12px]"
                    >
                      {fw.name}
                      {!enrolledKeys.has(fw.key) && !isEditMode && (
                        <span className="ml-2 text-[10px] text-text-placeholder">
                          (not yet enrolled)
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Audit type</Label>
              <Select
                value={auditType}
                onValueChange={(value) =>
                  setForm((current) => {
                    // Edit mode keeps the cycle's existing dates; create mode
                    // re-defaults the period for the new audit type.
                    if (isEditMode) return { ...current, auditType: value }
                    const start = current.periodStart ?? startOfToday()
                    return {
                      ...current,
                      auditType: value,
                      periodStart: start,
                      periodEnd: addMonths(
                        start,
                        defaultPeriodMonths(current.framework, value),
                      ),
                    }
                  })
                }
              >
                <SelectTrigger fullWidth className="h-9 border-border-divider text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectedFramework.auditTypes.map((t) => (
                    <SelectItem
                      key={t.value}
                      value={t.value}
                      className="text-[12px]"
                    >
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {willAutoEnroll && !atFrameworkLimit && (
            <div className="flex items-start gap-2 rounded-sm border border-status-blue-border bg-status-blue-subtle px-3 py-2">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-blue-text" />
              <p className="text-[11px] leading-[16px] text-status-blue-text">
                <span className="font-medium">
                  Creating this audit will enable {selectedFramework.name}
                </span>
                {` `}
                for this workspace. Controls, mappings, and policies will
                be seeded automatically. You can remove the framework later
                by deleting its audit.
              </p>
            </div>
          )}
          {/* #615: framework allowance exhausted — block + prompt upgrade. */}
          {atFrameworkLimit && allowance && (
            <div className="flex items-start gap-2 rounded-sm border border-status-amber-border bg-status-amber-badge-surface px-3 py-2">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-amber-strong" />
              <p className="text-[11px] leading-[16px] text-status-amber-strong">
                <span className="font-medium">
                  Your {allowance.planName} plan includes {allowance.limit}{' '}
                  framework{allowance.limit === 1 ? '' : 's'}.
                </span>
                {` `}
                You're already running {allowance.used}.{' '}
                <button
                  type="button"
                  onClick={showFrameworkLimitUpgrade}
                  className="font-medium underline underline-offset-2"
                >
                  Upgrade your plan
                </button>
                {` `}
                to enable {selectedFramework.name}.
              </p>
            </div>
          )}
          {/* #613: SOC 2 trust-service-criteria scope, declared on the cycle.
              Security is always required; the rest are opt-in. */}
          {framework === 'SOC2' && (
            <div className="space-y-1.5">
              <Label>Trust service criteria</Label>
              <CheckboxInput
                options={TSC_OPTIONS}
                value={trustServiceCriteria}
                onChange={(next) =>
                  setForm((current) => ({
                    ...current,
                    trustServiceCriteria: next.includes('Security')
                      ? next
                      : ['Security', ...next],
                  }))
                }
              />
            </div>
          )}
          {/* #613: HIPAA entity type, declared on the cycle. Drives the
              Privacy Officer role + which Privacy-Rule policies are required. */}
          {framework === 'HIPAA' && (
            <div className="space-y-1.5">
              <Label>HIPAA entity type</Label>
              <Select
                value={hipaaEntityType}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    hipaaEntityType: value as OnboardingHipaaEntityTypeValue,
                  }))
                }
              >
                <SelectTrigger fullWidth className="h-9 border-border-divider text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ONBOARDING_HIPAA_ENTITY_TYPE_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      className="text-[12px]"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] leading-[16px] text-text-subtle">
                Determines which HIPAA Privacy-Rule policies, roles, and
                evidence are required.
              </p>
            </div>
          )}
          {/* #813: No-PHI acknowledgment — required to scope a workspace into
              HIPAA before a BAA enables PHI. */}
          {showPhiAck && (
            <div className="flex items-start gap-2 rounded-sm border border-status-amber-border bg-status-amber-badge-surface px-3 py-2.5">
              <Checkbox
                id="noPhiAck"
                checked={noPhiAck}
                onCheckedChange={(checked) => setNoPhiAck(checked === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="noPhiAck"
                className="text-[11px] font-normal leading-[16px] text-status-amber-strong"
              >
                I agree not to upload, record, transmit, email, or store
                PHI/ePHI, patient data, medical records, or claims data in this
                workspace unless a Business Associate Agreement has been
                executed.
              </Label>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>
              Auditor firm
            </Label>
            <Select
              value={auditorId ?? '_none'}
              onValueChange={(value) =>
                setForm((current) => ({
                  ...current,
                  auditorId: value === '_none' ? undefined : value,
                }))
              }
            >
              <SelectTrigger fullWidth className="h-9 border-border-divider text-[12px]">
                <SelectValue placeholder="Select auditor firm (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none" className="text-[12px]">No auditor selected</SelectItem>
                {(auditorsQuery.data ?? []).map((auditor) => (
                  <SelectItem key={auditor.id} value={auditor.id} className="text-[12px]">
                    {auditor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="periodStart">
                Period start
              </Label>
              <DatePicker
                id="periodStart"
                value={periodStart}
                onChange={(date) =>
                  setForm((current) => {
                    if (!date) return { ...current, periodStart: date }
                    // Shift the end date to preserve the current period length
                    // so the user never has to re-pick it after changing the
                    // start (e.g. moving start into the past).
                    if (current.periodStart && current.periodEnd) {
                      const spanMs =
                        current.periodEnd.getTime() -
                        current.periodStart.getTime()
                      return {
                        ...current,
                        periodStart: date,
                        periodEnd: new Date(date.getTime() + spanMs),
                      }
                    }
                    // No prior span — default the end from the new start.
                    return {
                      ...current,
                      periodStart: date,
                      periodEnd: addMonths(
                        date,
                        defaultPeriodMonths(current.framework, current.auditType),
                      ),
                    }
                  })
                }
                placeholder="Select a start date"
                captionLayout="dropdown"
                className="h-9 rounded-sm border-border-divider text-[12px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="periodEnd">
                Period end
              </Label>
              <DatePicker
                id="periodEnd"
                value={periodEnd}
                onChange={(date) =>
                  setForm((current) => ({ ...current, periodEnd: date }))
                }
                placeholder="Select an end date"
                fromDate={periodStart}
                captionLayout="dropdown"
                className="h-9 rounded-sm border-border-divider text-[12px]"
              />
            </div>
          </div>
          {auditType === 'TYPE_I' && (
            <div className="space-y-1.5">
              <Label htmlFor="asOfDate">As-of date</Label>
              <DatePicker
                id="asOfDate"
                value={asOfDate ?? periodEnd}
                clearable={false}
                onChange={(date) => setForm((current) => ({ ...current, asOfDate: date }))}
                placeholder="Select an as-of date"
                captionLayout="dropdown"
                className="h-9 rounded-sm border-border-divider text-[12px]"
              />
            </div>
          )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-9 rounded-sm text-[13px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !label ||
                !periodStart ||
                !periodEnd ||
                isPending ||
                (showPhiAck && !noPhiAck) ||
                atFrameworkLimit
              }
              className="h-9 rounded-sm text-[13px]"
            >
              {isPending
                ? isEditMode
                  ? 'Saving...'
                  : 'Creating...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Create Audit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )

  return (
    <>
      {dialog}
      {upgradeDialog}
    </>
  )
}
